import type {
  ApproachRow,
  ContentClassRow,
  Exercise,
  ExerciseLevel,
  ExerciseTier,
  LevelCell,
  ScaleName,
  SessionPatternRow,
  TopicRow,
  Tonic,
} from 'Types/ExerciseContentTypes';
import type {
  ExercisePrePracticeReadModel,
  PrePracticeLevel,
  PrePracticeTier,
  TierProgressMap,
} from './prePracticeReadModel';
import { CIRCLE_OF_FIFTHS_TONICS } from 'Seeds/ExerciseContent/scaleLibraryCatalog';

/**
 * Fixture-as-read-model for the MUS-312 skeleton (Decision #131.1).
 *
 * These are the normalized MUS-308 rows for the one Phase 1 slice exercise,
 * "Play Scales from Notation" (scales-performance-read; BL-020, SP-03), with
 * deterministic local ids that mirror the authored MUS-311 slice. Pre-backend
 * this module stands in for the read endpoint; MUS-318 swaps the resolver at
 * the bottom for the real API and the screen is untouched.
 *
 * It is hand-authored rather than a resolution of the MUS-311 seed dump: that
 * dump lives on the stacked content branch, and the full nested read tree is
 * deferred to MUS-318 (v4 build-when-a-consumer-exists). The skeleton needs
 * only a representative read model of the real slice, which this is.
 */

// --- normalized rows (deterministic local ids) ------------------------------

const TOPIC: TopicRow = { id: 4, key: 'scales_keys', label: 'Scales & Keys', sort: 4 };

const APPROACH: ApproachRow = { id: 3, key: 'by_playing', label: 'by playing', sort: 3 };

const CONTENT_CLASS: ContentClassRow = {
  id: 20,
  code: 'BL-020',
  title: 'Play a scale from notation',
  description: null,
};

const SESSION_PATTERN: SessionPatternRow = {
  id: 3,
  code: 'SP-03',
  title: 'Repeat to clear, then master',
};

const EXERCISE: Exercise = {
  id: 1,
  slug: 'scales-performance-read',
  title: 'Play Scales from Notation',
  exercise_type_id: 1,
  content_class_id: CONTENT_CLASS.id,
  topic_id: TOPIC.id,
  approach_id: APPROACH.id,
  session_pattern_id: SESSION_PATTERN.id,
  clearance_policy_id: 1, // cumulative, n = 3
  mastery_policy_id: 2, // streak, n = 3
  createdAt: '2026-07-02T00:00:00.000Z',
  updatedAt: '2026-07-02T00:00:00.000Z',
  deletedAt: null,
};

/**
 * The templated tier triple, reused by every level (Decision #117.5). Values
 * mirror the authored slice's waypoints; `package_id` is filled per level below
 * (six shared packages: three content tiers + three review tiers).
 */
type TierTemplateRow = Omit<ExerciseTier, 'id' | 'exercise_level_id' | 'package_id'>;

const TIER_TEMPLATE: TierTemplateRow[] = [
  {
    tier_number: 1,
    wp_hand: 'HT',
    wp_range_octaves: 1,
    wp_direction: 'asc',
    wp_rhythm_density: 'quarter',
    wp_tempo: 60,
  },
  {
    tier_number: 2,
    wp_hand: 'HT',
    wp_range_octaves: 2,
    wp_direction: 'both',
    wp_rhythm_density: 'eighth',
    wp_tempo: 92,
  },
  {
    tier_number: 3,
    wp_hand: 'HT',
    wp_range_octaves: 4,
    wp_direction: 'both',
    wp_rhythm_density: 'sixteenth',
    wp_tempo: 120,
  },
];

const SCALE: ScaleName = 'major';

interface LevelSeed {
  level_number: number;
  title: string;
  is_review: boolean;
  description: string | null;
  tonics: Tonic[];
}

/**
 * A scale unit is one scale/mode holding its 12 tonic-cells (one `level_cell`
 * coordinate per key), tonics in circle-of-fifths display order - the one
 * shared order, imported from the catalog so the fixture cannot drift from it.
 * The four 3-key subgroup levels and the separate 12-key review level are
 * retired: one unit, twelve cells, reachable freely (the in-unit full-12
 * gauntlet is the review's successor). The unit's user-facing name is the
 * scale's own name.
 */
const LEVEL_SEEDS: LevelSeed[] = [
  {
    level_number: 1,
    title: 'Major',
    is_review: false,
    description: 'The major scale in all twelve keys.',
    tonics: [...CIRCLE_OF_FIFTHS_TONICS],
  },
];

const LEVELS: ExerciseLevel[] = LEVEL_SEEDS.map((seed, index) => ({
  id: index + 1,
  exercise_id: EXERCISE.id,
  level_number: seed.level_number,
  title: seed.title,
  is_review: seed.is_review,
  description: seed.description,
}));

const TIERS: ExerciseTier[] = LEVELS.flatMap((level) =>
  TIER_TEMPLATE.map((tier, tierIndex) => ({
    ...tier,
    id: level.id * 10 + (tierIndex + 1),
    exercise_level_id: level.id,
    // Six shared packages: 1-3 content, 4-6 review.
    package_id: tier.tier_number + (level.is_review ? 3 : 0),
  })),
);

/**
 * The `level_cell` rows (schema 3.2): one (scale, tonic) coordinate per key a
 * level scopes. The seed's `LEVEL_SEEDS[i].tonics` are aligned to `LEVELS[i]` by
 * construction (both map over `LEVEL_SEEDS` in order).
 */
const LEVEL_CELLS: LevelCell[] = LEVELS.flatMap((level, index) =>
  LEVEL_SEEDS[index].tonics.map((tonic, tonicIndex) => ({
    id: level.id * 100 + (tonicIndex + 1),
    exercise_level_id: level.id,
    scale: SCALE,
    tonic,
  })),
);

// --- resolver: normalized rows -> read model --------------------------------
// The join MUS-318's read endpoint will perform server-side.

function resolveTier(row: ExerciseTier): PrePracticeTier {
  return {
    tierNumber: row.tier_number,
    hand: row.wp_hand,
    rangeOctaves: row.wp_range_octaves,
    direction: row.wp_direction,
    rhythmDensity: row.wp_rhythm_density,
    tempo: row.wp_tempo,
  };
}

function resolveLevel(level: ExerciseLevel): PrePracticeLevel {
  return {
    levelNumber: level.level_number,
    title: level.title,
    isReview: level.is_review,
    description: level.description,
    tiers: TIERS.filter((tier) => tier.exercise_level_id === level.id)
      .sort((a, b) => a.tier_number - b.tier_number)
      .map(resolveTier),
    cells: LEVEL_CELLS.filter((cell) => cell.exercise_level_id === level.id).map(
      ({ scale, tonic }) => ({
        scale,
        tonic,
      }),
    ),
  };
}

/**
 * Off-by-default demo grip for local eyeballing ONLY. The skeleton ships with
 * NO grip (the honest day-one empty state), because inventing progress would be
 * fabricated territory (#124). To see the four tier states light up
 * locally, pass this into the resolver: `getExercisePrePracticeReadModel(DEMO_PROGRESS)`.
 * Never wire it as the default and never commit it as the shipped state.
 */
export const DEMO_PROGRESS: TierProgressMap = {
  '1:1': 'mastered',
  '1:2': 'mastered',
  '1:3': 'mastered',
  '2:1': 'cleared',
  '2:2': 'attempted',
};

/**
 * Resolve the slice exercise into the pre-practice read model.
 *
 * `progress` defaults to undefined, so the shipped skeleton renders every tier
 * untouched. MUS-318 will pass real grip here (or the read endpoint will carry
 * it) with no screen edit.
 */
export function getExercisePrePracticeReadModel(
  progress?: TierProgressMap,
): ExercisePrePracticeReadModel {
  return {
    exerciseId: EXERCISE.id,
    slug: EXERCISE.slug,
    title: EXERCISE.title,
    description: CONTENT_CLASS.description,
    classification: {
      topicLabel: TOPIC.label,
      approachLabel: APPROACH.label,
      contentClassCode: CONTENT_CLASS.code,
      sessionPatternCode: SESSION_PATTERN.code,
    },
    levels: [...LEVELS].sort((a, b) => a.level_number - b.level_number).map(resolveLevel),
    progress,
  };
}
